import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Newsletter = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API}/newsletter/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: "Welcome to SYNTETIK",
          description: t('newsletter.success'),
        });
        setEmail('');
      } else {
        toast({
          title: "Error",
          description: t('newsletter.error'),
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: t('newsletter.error'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="join" className="py-24 px-6 bg-gradient-to-b from-gray-900/30 to-black relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-blue-400/20 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-radial from-white/10 to-transparent rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-7xl font-light tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-white mb-8">
            {t('newsletter.title')}
          </h2>
          <p className="text-xl text-gray-300 font-light max-w-2xl mx-auto leading-relaxed">
            {t('newsletter.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="max-w-lg mx-auto">
          <div className="flex flex-col sm:flex-row gap-4">
            <Input
              type="email"
              placeholder={t('newsletter.placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-black/40 border-white/20 text-white placeholder:text-gray-400 focus:border-blue-400 focus:ring-blue-400/20 rounded-none h-14 text-lg backdrop-blur-sm"
              required
              disabled={isSubmitting}
            />
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-transparent border border-white/30 text-white hover:bg-white hover:text-black hover:scale-105 transform transition-all duration-300 px-8 h-14 text-lg font-light tracking-widest rounded-none backdrop-blur-sm disabled:opacity-50"
            >
              {isSubmitting ? 'JOINING...' : t('newsletter.button')}
            </Button>
          </div>
        </form>

        <div className="grid md:grid-cols-2 gap-8 mt-16 max-w-2xl mx-auto">
          <div className="text-center group">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-blue-400/20 to-white/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-300 to-white rounded-full opacity-60"></div>
            </div>
            <h3 className="text-white font-light text-xl mb-2 group-hover:text-blue-200 transition-colors">Exclusive Releases</h3>
            <p className="text-gray-400 font-light">Be first to experience new compositions</p>
          </div>
          
          <div className="text-center group">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-blue-400/20 to-white/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-300 to-white rounded-full opacity-60"></div>
            </div>
            <h3 className="text-white font-light text-xl mb-2 group-hover:text-blue-200 transition-colors">Behind the Scenes</h3>
            <p className="text-gray-400 font-light">Insights into the creative process</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;